import React, { useRef, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';

interface Service {
  id: number;
  title: string;
  description: string;
  image: string;
  benefits: string[];
}

interface Content {
  title: string;
  subtitle: string;
  services: Service[];
}

interface ServicesContent {
  en: Content;
  da: Content;
}

const Services: React.FC<{ language: string }> = ({ language }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const content: ServicesContent = {
    en: {
      title: "Our Premium Treatments",
      subtitle: "We offer a range of advanced aesthetic procedures tailored to enhance your natural beauty and boost your confidence.",
      services: [
        {
          id: 1,
          title: "PRP Therapy",
          description: "Platelet-Rich Plasma therapy uses your own blood platelets to stimulate skin renewal, improve texture, and reduce signs of aging.",
          image: "https://l0ry9zjg4l.ufs.sh/f/70f42tTcLu8n6R9kwAiBQ5XKN27otwPyu3DAmSIj6ET1gY9L",
          benefits: [
            "Natural skin rejuvenation",
            "Improved texture and tone",
            "Reduced fine lines and wrinkles",
            "Enhanced collagen production"
          ]
        },
        {
          id: 2,
          title: "Botox Treatment",
          description: "A non-surgical procedure that temporarily smooths moderate to severe wrinkles in areas like the forehead, between the brows, and crow's feet.",
          image: "https://l0ry9zjg4l.ufs.sh/f/70f42tTcLu8nHM5HJoCaGopgS6V1dAElrCtvn3ysLImfhjaJ",
          benefits: [
            "Smooths facial wrinkles",
            "Preventative anti-aging",
            "Quick, minimal downtime",
            "Natural-looking results"
          ]
        },
        {
          id: 3,
          title: "Dermal Fillers",
          description: "Injectable treatments that restore volume, smooth lines, and enhance facial contours for a more youthful appearance.",
          image: "https://l0ry9zjg4l.ufs.sh/f/70f42tTcLu8n18wuIy6QDTgjtrcBpP2zG8KmVksvU6SaCYF9",
          benefits: [
            "Instant volume restoration",
            "Enhanced facial contours",
            "Long-lasting results",
            "Natural feel and movement"
          ]
        },
        {
          id: 4,
          title: "Mesotherapy",
          description: "A minimally invasive procedure that delivers vitamins, minerals, and amino acids directly into the skin to improve its appearance and health.",
          image: "https://l0ry9zjg4l.ufs.sh/f/70f42tTcLu8n6H6NOMiBQ5XKN27otwPyu3DAmSIj6ET1gY9L",
          benefits: [
            "Deep skin hydration",
            "Improved skin texture",
            "Enhanced skin glow",
            "Targeted treatment"
          ]
        }
      ]
    },
    da: {
      title: "Vores Premium Behandlinger",
      subtitle: "Vi tilbyder en række avancerede æstetiske procedurer, der er skræddersyet til at fremhæve din naturlige skønhed og øge din selvtillid.",
      services: [
        {
          id: 1,
          title: "PRP Behandling",
          description: "Platelet-Rich Plasma behandling bruger dine egne blodplader til at stimulere hudfornyelse, forbedre tekstur og reducere alderstegn.",
          image: "https://l0ry9zjg4l.ufs.sh/f/70f42tTcLu8n6R9kwAiBQ5XKN27otwPyu3DAmSIj6ET1gY9L",
          benefits: [
            "Naturlig hudfornyelse",
            "Forbedret tekstur og tone",
            "Reducerede fine linjer og rynker",
            "Øget kollagenproduktion"
          ]
        },
        {
          id: 2,
          title: "Botox Behandling",
          description: "En ikke-kirurgisk procedure, der midlertidigt glatter moderate til svære rynker i områder som panden, mellem brynene og omkring øjnene.",
          image: "https://l0ry9zjg4l.ufs.sh/f/70f42tTcLu8nHM5HJoCaGopgS6V1dAElrCtvn3ysLImfhjaJ",
          benefits: [
            "Glatter ansigtsrynker",
            "Forebyggende anti-aging",
            "Hurtig, minimal restitutionstid",
            "Naturligt udseende resultater"
          ]
        },
        {
          id: 3,
          title: "Dermal Filler",
          description: "Injicerbare behandlinger der gendanner volumen, glatter linjer og forbedrer ansigtskonturer for et mere ungdommeligt udseende.",
          image: "https://l0ry9zjg4l.ufs.sh/f/70f42tTcLu8n18wuIy6QDTgjtrcBpP2zG8KmVksvU6SaCYF9",
          benefits: [
            "Øjeblikkelig volumengenoprettelse",
            "Forbedrede ansigtskonturer",
            "Langvarige resultater",
            "Naturlig følelse og bevægelse"
          ]
        },
        {
          id: 4,
          title: "Mesoterapibehandling",
          description: "En minimalt invasiv procedure, der leverer vitaminer, mineraler og aminosyrer direkte i huden for at forbedre dens udseende og sundhed.",
          image: "https://l0ry9zjg4l.ufs.sh/f/70f42tTcLu8n6H6NOMiBQ5XKN27otwPyu3DAmSIj6ET1gY9L",
          benefits: [
            "Dyb hudhydrering",
            "Forbedret hudtekstur",
            "Øget hudglød",
            "Målrettet behandling"
          ]
        }
      ]
    }
  };

  return (
    <section className="py-20 bg-white" id="services">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <div className="relative inline-block">
            <h2 
              className="text-3xl md:text-4xl font-serif font-bold mb-4 relative z-10"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {content[language as keyof ServicesContent].title}
              <div className="absolute -bottom-2 left-0 w-full h-1 bg-[#A68B7C]/20 transform -skew-x-12"></div>
            </h2>
          </div>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto mt-6">
            {content[language as keyof ServicesContent].subtitle}
          </p>
        </div>
        
        <div ref={ref} className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {content[language as keyof ServicesContent].services.map((service, index) => (
            <div 
              key={service.id}
              className={`group transform transition-all duration-500 hover:scale-[1.02] hover:-translate-y-2 ${
                inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{
                transitionDelay: `${index * 200}ms`
              }}
            >
              <div className="relative bg-white rounded-2xl overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.12)] transition-all duration-500 group-hover:shadow-[0_20px_40px_rgb(0,0,0,0.16)] border border-gray-100">
                <div className="relative h-64 overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/50 z-10" />
                  <img
                    src={service.image}
                    alt={service.title}
                    className="absolute inset-0 w-full h-full object-cover transform transition-transform duration-700 group-hover:scale-110 lazy-load"
                    loading="lazy"
                    decoding="async"
                    fetchPriority={index < 2 ? "high" : "low"}
                    onLoad={(e) => e.currentTarget.classList.add('loaded')}
                  />
                  <h3 
                    className="absolute bottom-4 left-6 text-2xl font-serif font-semibold text-white z-20"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {service.title}
                  </h3>
                </div>
                
                <div className="p-6 space-y-4">
                  <p className="text-gray-600 leading-relaxed">
                    {service.description}
                  </p>
                  
                  <ul className="space-y-2">
                    {service.benefits.map((benefit, idx) => (
                      <li 
                        key={idx}
                        className="flex items-center text-sm text-gray-600"
                      >
                        <div className="w-1.5 h-1.5 rounded-full bg-[#A68B7C]/60 mr-2" />
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div 
                  className="absolute inset-0 bg-gradient-to-tr from-[#A68B7C]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;